import { Link } from "react-router-dom";
import { ArrowRight, Check, CalendarRange, Gauge, ClipboardList } from "lucide-react";
import { Seo } from "../components/Seo";
import { FadeUp, Stagger, StaggerItem } from "../components/Reveal";
import { PILLARS } from "../data/content";

const CYCLES = [
    {
        phase: "Days 1–30",
        title: "Baseline & priorities",
        desc: "Kick-off session and baseline scorecard across all six areas. We lock in no more than 3–5 priorities for the programme and assign owners and due dates.",
    },
    {
        phase: "Days 31–60",
        title: "Execution cycle",
        desc: "Weekly rhythm on sales, margin and channel actions. KPI and action tracker live. Commercial problem-solving support as issues surface.",
    },
    {
        phase: "Days 61–90",
        title: "Embed & hand over",
        desc: "Second execution cycle completes. End-of-programme scorecard measures the shift, and we agree the next-stage plan — often an Advisory Retainer.",
    },
];

const INCLUDES = [
    "Kick-off and baseline scorecard",
    "Three 30-day execution cycles",
    "Sales, margin and channel priorities based on your diagnostic",
    "Management KPI and action tracker",
    "Owner and manager review sessions",
    "Commercial problem-solving support throughout",
    "End-of-programme scorecard and next-stage plan",
];

const PROOF_POINTS = [
    { icon: CalendarRange, text: "90-day structured engagement with a defined start and end" },
    { icon: Gauge, text: "Baseline and end-of-programme scorecards, so progress is measured" },
    { icon: ClipboardList, text: "A living action tracker — every action has an owner and a due date" },
];

export default function GrowVise90() {
    return (
        <div data-testid="growvise-90-page">
            <Seo
                title="GrowVise 90 — 90-Day Growth & Execution Programme"
                description="A structured 90-day commercial growth programme for NZ product businesses: baseline scorecard, three execution cycles, KPI tracking and owner accountability."
                path="/growvise-90"
            />

            {/* Header — navy */}
            <section className="bg-navy-950 pt-32 sm:pt-40 pb-16 sm:pb-20" data-testid="g90-hero">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <FadeUp>
                        <p className="eyebrow">GrowVise 90 · Flagship Programme</p>
                        <h1 className="mt-6 font-serif font-semibold text-white tracking-tight leading-[1.15] text-4xl sm:text-5xl max-w-2xl">
                            Ninety days. One plan. Real execution.
                        </h1>
                        <p className="mt-6 text-base sm:text-lg text-slate-300/90 leading-relaxed max-w-xl">
                            For businesses that know what needs to improve but need structure, commercial judgment and accountability to actually get it done.
                        </p>
                        <div className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-4">
                            <Link to="/book-a-conversation" className="btn-gold" data-testid="g90-book-cta">
                                Book a Growth Conversation
                            </Link>
                            <Link to="/business-health-check" className="inline-flex items-center gap-2 text-sm font-medium text-gold-400 hover:text-gold-300 transition-colors duration-200" data-testid="g90-assessment-cta">
                                Try the free Health Check <ArrowRight size={15} />
                            </Link>
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* Cycles — white editorial rows */}
            <section className="section-pad bg-white" data-testid="g90-cycles">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <FadeUp className="max-w-xl mb-14">
                        <p className="eyebrow-light">How the programme runs</p>
                        <h2 className="mt-5 font-serif font-semibold text-ink tracking-tight leading-snug text-3xl sm:text-4xl">
                            Three 30-day execution cycles.
                        </h2>
                    </FadeUp>
                    <Stagger>
                        {CYCLES.map((c, i) => (
                            <StaggerItem key={c.phase}>
                                <div className="grid sm:grid-cols-[70px_220px_1fr] gap-3 sm:gap-8 py-7 border-t border-navy-900/10 items-baseline" data-testid={`g90-cycle-${i + 1}`}>
                                    <p className="font-serif font-semibold text-3xl text-navy-900/15">{String(i + 1).padStart(2, "0")}</p>
                                    <div>
                                        <p className="font-mono text-[11px] tracking-widest uppercase text-gold-700">{c.phase}</p>
                                        <h3 className="font-serif font-semibold text-ink text-xl mt-1.5">{c.title}</h3>
                                    </div>
                                    <p className="text-warmgrey leading-relaxed">{c.desc}</p>
                                </div>
                            </StaggerItem>
                        ))}
                    </Stagger>
                    <FadeUp className="mt-12">
                        <p className="label-mono">Assessed and managed across six areas</p>
                        <div className="flex flex-wrap gap-2.5" data-testid="g90-pillar-chips">
                            {PILLARS.map((p) => (
                                <span key={p.id} className="font-mono text-[11px] tracking-widest uppercase text-navy-800 border border-navy-900/20 bg-white rounded-full px-4 py-2">
                                    {p.label}
                                </span>
                            ))}
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* Inclusions — cream */}
            <section className="section-pad bg-cream border-t border-navy-900/10" data-testid="g90-includes">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                    <FadeUp className="lg:col-span-5">
                        <p className="eyebrow-light">What you get</p>
                        <h2 className="mt-5 font-serif font-semibold text-ink tracking-tight leading-snug text-3xl sm:text-4xl">
                            Structure, judgment and accountability — for 90 days.
                        </h2>
                        <div className="mt-8 space-y-5">
                            {PROOF_POINTS.map((p) => (
                                <div key={p.text} className="flex items-start gap-4">
                                    <p.icon size={19} className="text-gold-700 mt-0.5 shrink-0" />
                                    <p className="text-sm text-ink/85 leading-relaxed">{p.text}</p>
                                </div>
                            ))}
                        </div>
                        <p className="mt-10 font-mono text-lg text-gold-700" data-testid="g90-price">From $6,000 + GST</p>
                        <p className="mt-2 text-[11px] font-mono tracking-widest uppercase text-navy-900/45">Scope agreed in proposal — founding-client terms available for pilot businesses</p>
                        <Link to="/book-a-conversation" className="btn-gold mt-8" data-testid="g90-enquire-cta">
                            Discuss GrowVise 90 <ArrowRight size={15} />
                        </Link>
                    </FadeUp>
                    <Stagger className="lg:col-span-6 lg:col-start-7 card-light p-8 sm:p-10">
                        <p className="label-mono mb-4">Programme inclusions</p>
                        {INCLUDES.map((item) => (
                            <StaggerItem key={item}>
                                <div className="flex items-start gap-3 py-3 border-t border-navy-900/10 first:border-t-0">
                                    <Check size={16} className="text-gold-600 mt-0.5 shrink-0" />
                                    <p className="text-sm text-ink/85">{item}</p>
                                </div>
                            </StaggerItem>
                        ))}
                    </Stagger>
                </div>
            </section>
        </div>
    );
}
